import React, { useState } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { LayoutDashboard, Music2, Users, Calendar, Mail, LogOut, Menu, X, Image, Handshake, Info, Settings, Shield, Key, Images, Radio, FolderHeart } from 'lucide-react';

export const AdminLayout = () => {
  const { logout, admin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const navItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/hero-slides', icon: Image, label: 'Hero Slides' },
    { path: '/admin/releases', icon: Music2, label: 'Releases' },
    { path: '/admin/artists', icon: Users, label: 'Artists' },
    { path: '/admin/events', icon: Calendar, label: 'Events' },
    { path: '/admin/gallery', icon: Images, label: 'Gallery' },
    { path: '/admin/party-albums', icon: FolderHeart, label: 'Party Albums' },
    { path: '/admin/radio', icon: Radio, label: 'Radio' },
    { path: '/admin/about', icon: Info, label: 'About' },
    { path: '/admin/partners', icon: Handshake, label: 'Partners' },
    { path: '/admin/contact-settings', icon: Settings, label: 'Contact Settings' },
    { path: '/admin/contacts', icon: Mail, label: 'Messages' },
    { path: '/admin/users', icon: Shield, label: 'User Management' },
    { path: '/admin/password', icon: Key, label: 'Change Password' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-orange-950/10 to-black">
      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-md border-b border-orange-500/30 px-4 py-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-green-400">
            DHARMAR ADMIN
          </h1>
          <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="text-white">
            {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-black/80 backdrop-blur-md border-r border-orange-500/30 z-40 transition-transform duration-300
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        md:translate-x-0
      `}>
        <div className="p-6 border-b border-orange-500/30">
          <h1 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-green-400">
            DHARMAR
          </h1>
          <p className="text-gray-400 text-sm mt-1">Admin Panel</p>
        </div>

        <nav className="p-4 space-y-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsSidebarOpen(false)}
                className={`
                  flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200
                  ${isActive(item.path)
                    ? 'bg-gradient-to-r from-orange-500/20 to-green-500/20 border border-orange-500/50 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }
                `}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-orange-500/30">
          <div className="mb-4 px-4">
            <p className="text-sm text-gray-400">Logged in as</p>
            <p className="text-white font-medium">{admin?.username}</p>
          </div>
          <Button
            onClick={handleLogout}
            className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="md:ml-64 min-h-screen pt-20 md:pt-0 p-8">
        <Outlet />
      </main>
    </div>
  );
};
